
import { useState, useEffect } from 'react';

interface HeroProps {
  navBackground: boolean;
}

export default function Hero({ navBackground }: HeroProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    contactSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNavClick = (id: string) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || mobileMenuOpen ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img
              src="https://static.readdy.ai/image/ec7d44241e5d5731ab0de78142a46920/55979cdc92171d0792a109e88caba25d.png"
              alt="Premier Renovations Logo"
              className="h-9 sm:h-12 w-auto"
            />
            <span
              className={`text-base sm:text-xl font-bold transition-colors ${
                isScrolled || mobileMenuOpen ? 'text-[#0B1F3A]' : 'text-white'
              }`}
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Premier Renovations
            </span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <a
              href="#about"
              className={`transition-colors hover:text-[#2D6CDF] ${
                isScrolled ? 'text-[#0B1F3A]' : 'text-white'
              }`}
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              About
            </a>
            <a
              href="#services"
              className={`transition-colors hover:text-[#2D6CDF] ${
                isScrolled ? 'text-[#0B1F3A]' : 'text-white'
              }`}
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Services
            </a>
            <a
              href="#gallery"
              className={`transition-colors hover:text-[#2D6CDF] ${
                isScrolled ? 'text-[#0B1F3A]' : 'text-white'
              }`}
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Gallery
            </a>
            <a
              href="#contact"
              className={`transition-colors hover:text-[#2D6CDF] ${
                isScrolled ? 'text-[#0B1F3A]' : 'text-white'
              }`}
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Contact
            </a>
            <a
              href="tel:316-867-6848"
              className={`transition-colors hover:text-[#2D6CDF] ${
                isScrolled ? 'text-[#0B1F3A]' : 'text-white'
              }`}
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              316-867-6848
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i
              className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${
                isScrolled || mobileMenuOpen ? 'text-[#0B1F3A]' : 'text-white'
              }`}
            ></i>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-6 pt-2">
            <div className="flex flex-col gap-4">
              <a
                onClick={() => handleNavClick('about')}
                className="text-[#0B1F3A] text-base py-2 cursor-pointer hover:text-[#2D6CDF] transition-colors"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                About
              </a>
              <a
                onClick={() => handleNavClick('services')}
                className="text-[#0B1F3A] text-base py-2 cursor-pointer hover:text-[#2D6CDF] transition-colors"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Services
              </a>
              <a
                onClick={() => handleNavClick('gallery')}
                className="text-[#0B1F3A] text-base py-2 cursor-pointer hover:text-[#2D6CDF] transition-colors"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Gallery
              </a>
              <a
                onClick={() => handleNavClick('contact')}
                className="text-[#0B1F3A] text-base py-2 cursor-pointer hover:text-[#2D6CDF] transition-colors"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Contact
              </a>
              <a
                href="tel:316-867-6848"
                className="text-[#0B1F3A] text-base py-2 cursor-pointer hover:text-[#2D6CDF] transition-colors"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                316-867-6848
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/ec7d44241e5d5731ab0de78142a46920/10d465263a8f09760d4e2455b3967e15.png"
          alt="Luxury Kitchen"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 w-full">
          <div className="max-w-2xl">
            <h1
              className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                letterSpacing: '-0.02em',
              }}
            >
              Transform Your Kitchen and Bathroom with Premier Craftsmanship
            </h1>
            <p
              className="text-white/70 text-base sm:text-lg md:text-xl mb-8 sm:mb-12 leading-relaxed"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Affordable Kitchen and Bath Solutions
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <button
                onClick={scrollToContact}
                className="bg-[#0B1F3A] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#2D6CDF] transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Get a Free Estimate
              </button>
              <button
                onClick={() =>
                  document
                    .getElementById('services')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
                className="border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0B1F3A] transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                View Our Work
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
